export interface TemplateField {
  name: string
  label: string
  type: 'text' | 'password' | 'email' | 'url' | 'number' | 'textarea' | 'select'
  placeholder?: string
  required?: boolean
  defaultValue?: string
  options?: { value: string; label: string }[] // For select type
  mask?: boolean // For sensitive fields like passwords
}

export interface SecretTemplate {
  id: string
  name: string
  description: string
  icon: string // Icon name from lucide-react
  fields: TemplateField[]
  category: 'credentials' | 'development' | 'personal' | 'network'
}

export const secretTemplates: SecretTemplate[] = [
  {
    id: 'api-credentials',
    name: 'API Credentials',
    description: 'API keys and secrets',
    icon: 'Code',
    category: 'development',
    fields: [
      {
        name: 'service',
        label: 'Service Name',
        type: 'text',
        placeholder: 'e.g., OpenAI, Stripe, AWS',
        required: true
      },
      {
        name: 'apiKey',
        label: 'API Key',
        type: 'password',
        placeholder: 'sk-...',
        required: true,
        mask: true
      },
      {
        name: 'apiSecret',
        label: 'API Secret',
        type: 'password',
        placeholder: 'Secret key (if applicable)',
        mask: true
      },
      {
        name: 'endpoint',
        label: 'API Endpoint',
        type: 'url',
        placeholder: 'https://api.example.com/v1'
      },
      {
        name: 'environment',
        label: 'Environment',
        type: 'select',
        defaultValue: 'production',
        options: [
          { value: 'production', label: 'Production' },
          { value: 'staging', label: 'Staging' },
          { value: 'development', label: 'Development' },
          { value: 'test', label: 'Test' }
        ]
      },
      {
        name: 'notes',
        label: 'Additional Notes',
        type: 'textarea',
        placeholder: 'Rate limits, special headers, etc.'
      }
    ]
  },
  {
    id: 'database',
    name: 'Database Connection',
    description: 'Database credentials and connection info',
    icon: 'Database',
    category: 'development',
    fields: [
      {
        name: 'dbType',
        label: 'Database Type',
        type: 'select',
        defaultValue: 'postgresql',
        required: true,
        options: [
          { value: 'postgresql', label: 'PostgreSQL' },
          { value: 'mysql', label: 'MySQL' },
          { value: 'mongodb', label: 'MongoDB' },
          { value: 'redis', label: 'Redis' },
          { value: 'sqlite', label: 'SQLite' },
          { value: 'mssql', label: 'SQL Server' }
        ]
      },
      {
        name: 'host',
        label: 'Host',
        type: 'text',
        placeholder: 'localhost or IP address',
        required: true
      },
      {
        name: 'port',
        label: 'Port',
        type: 'number',
        placeholder: '5432',
        defaultValue: '5432'
      },
      {
        name: 'database',
        label: 'Database Name',
        type: 'text',
        placeholder: 'myapp_production',
        required: true
      },
      {
        name: 'username',
        label: 'Username',
        type: 'text',
        placeholder: 'db_user',
        required: true
      },
      {
        name: 'password',
        label: 'Password',
        type: 'password',
        placeholder: '••••••••',
        required: true,
        mask: true
      },
      {
        name: 'connectionString',
        label: 'Connection String (Alternative)',
        type: 'textarea',
        placeholder: 'postgresql://user:pass@host:port/db',
        mask: true
      }
    ]
  },
  {
    id: 'login',
    name: 'Login Credentials',
    description: 'Website or app login details',
    icon: 'LogIn',
    category: 'credentials',
    fields: [
      {
        name: 'service',
        label: 'Service/Website',
        type: 'text',
        placeholder: 'e.g., GitHub, Gmail, AWS Console',
        required: true
      },
      {
        name: 'url',
        label: 'Login URL',
        type: 'url',
        placeholder: 'https://example.com/login'
      },
      {
        name: 'username',
        label: 'Username/Email',
        type: 'text',
        placeholder: 'user@example.com',
        required: true
      },
      {
        name: 'password',
        label: 'Password',
        type: 'password',
        placeholder: '••••••••',
        required: true,
        mask: true
      },
      {
        name: 'totpSecret',
        label: '2FA/TOTP Secret',
        type: 'password',
        placeholder: 'Base32 secret for authenticator apps',
        mask: true
      },
      {
        name: 'backupCodes',
        label: 'Backup Codes',
        type: 'textarea',
        placeholder: 'One-time backup codes (one per line)',
        mask: true
      },
      {
        name: 'notes',
        label: 'Notes',
        type: 'textarea',
        placeholder: 'Security questions, special instructions, etc.'
      }
    ]
  },
  {
    id: 'ssh',
    name: 'SSH Key',
    description: 'SSH private key and connection details',
    icon: 'Terminal',
    category: 'development',
    fields: [
      {
        name: 'server',
        label: 'Server/Host',
        type: 'text',
        placeholder: 'example.com or IP address',
        required: true
      },
      {
        name: 'port',
        label: 'Port',
        type: 'number',
        placeholder: '22',
        defaultValue: '22'
      },
      {
        name: 'username',
        label: 'Username',
        type: 'text',
        placeholder: 'root',
        defaultValue: 'root'
      },
      {
        name: 'privateKey',
        label: 'Private Key',
        type: 'textarea',
        placeholder: '-----BEGIN RSA PRIVATE KEY-----\n...\n-----END RSA PRIVATE KEY-----',
        required: true,
        mask: true
      },
      {
        name: 'passphrase',
        label: 'Key Passphrase',
        type: 'password',
        placeholder: 'Passphrase for the private key (if any)',
        mask: true
      },
      {
        name: 'notes',
        label: 'Notes',
        type: 'textarea',
        placeholder: 'Jump hosts, special commands, etc.'
      }
    ]
  },
  {
    id: 'wifi',
    name: 'WiFi Password',
    description: 'Network name and password',
    icon: 'Wifi',
    category: 'network',
    fields: [
      {
        name: 'ssid',
        label: 'Network Name (SSID)',
        type: 'text',
        placeholder: 'MyHomeWiFi',
        required: true
      },
      {
        name: 'password',
        label: 'Password',
        type: 'password',
        placeholder: '••••••••',
        required: true,
        mask: true
      },
      {
        name: 'security',
        label: 'Security Type',
        type: 'select',
        defaultValue: 'wpa2',
        options: [
          { value: 'wpa2', label: 'WPA2' },
          { value: 'wpa3', label: 'WPA3' },
          { value: 'wep', label: 'WEP (Not Secure)' },
          { value: 'open', label: 'Open (No Password)' }
        ]
      },
      {
        name: 'hidden',
        label: 'Hidden Network',
        type: 'select',
        defaultValue: 'no',
        options: [
          { value: 'no', label: 'No' },
          { value: 'yes', label: 'Yes' }
        ]
      },
      {
        name: 'notes',
        label: 'Notes',
        type: 'textarea',
        placeholder: 'Router location, admin password, etc.'
      }
    ]
  },
  {
    id: 'credit-card',
    name: 'Credit Card',
    description: 'Card details for sharing',
    icon: 'CreditCard',
    category: 'personal',
    fields: [
      {
        name: 'cardType',
        label: 'Card Type',
        type: 'select',
        defaultValue: 'visa',
        options: [
          { value: 'visa', label: 'Visa' },
          { value: 'mastercard', label: 'Mastercard' },
          { value: 'amex', label: 'American Express' },
          { value: 'discover', label: 'Discover' },
          { value: 'other', label: 'Other' }
        ]
      },
      {
        name: 'cardNumber',
        label: 'Card Number',
        type: 'text',
        placeholder: '4111 1111 1111 1111',
        required: true,
        mask: true
      },
      {
        name: 'cardholderName',
        label: 'Cardholder Name',
        type: 'text',
        placeholder: 'John Doe',
        required: true
      },
      {
        name: 'expiry',
        label: 'Expiry Date',
        type: 'text',
        placeholder: 'MM/YY',
        required: true
      },
      {
        name: 'cvv',
        label: 'CVV/Security Code',
        type: 'text',
        placeholder: '123',
        required: true,
        mask: true
      },
      {
        name: 'billingZip',
        label: 'Billing ZIP/Postal Code',
        type: 'text',
        placeholder: '12345'
      },
      {
        name: 'pin',
        label: 'PIN (if applicable)',
        type: 'password',
        placeholder: '••••',
        mask: true
      }
    ]
  },
  {
    id: 'env-file',
    name: 'Environment Variables',
    description: '.env file contents',
    icon: 'FileCode',
    category: 'development',
    fields: [
      {
        name: 'project',
        label: 'Project Name',
        type: 'text',
        placeholder: 'my-app',
        required: true
      },
      {
        name: 'environment',
        label: 'Environment',
        type: 'select',
        defaultValue: 'production',
        options: [
          { value: 'production', label: 'Production' },
          { value: 'staging', label: 'Staging' },
          { value: 'development', label: 'Development' },
          { value: 'test', label: 'Test' }
        ]
      },
      {
        name: 'envVars',
        label: 'Environment Variables',
        type: 'textarea',
        placeholder: 'DATABASE_URL=postgresql://...\nAPI_KEY=sk-...\nSECRET_KEY=...',
        required: true,
        mask: true
      },
      {
        name: 'notes',
        label: 'Setup Instructions',
        type: 'textarea',
        placeholder: 'Copy to .env file in project root, restart server, etc.'
      }
    ]
  },
  {
    id: 'crypto-wallet',
    name: 'Crypto Wallet',
    description: 'Wallet seed phrase or private key',
    icon: 'Wallet',
    category: 'personal',
    fields: [
      {
        name: 'walletType',
        label: 'Wallet Type',
        type: 'select',
        defaultValue: 'metamask',
        options: [
          { value: 'metamask', label: 'MetaMask' },
          { value: 'ledger', label: 'Ledger' },
          { value: 'trezor', label: 'Trezor' },
          { value: 'coinbase', label: 'Coinbase Wallet' },
          { value: 'trustwallet', label: 'Trust Wallet' },
          { value: 'other', label: 'Other' }
        ]
      },
      {
        name: 'network',
        label: 'Network',
        type: 'select',
        defaultValue: 'ethereum',
        options: [
          { value: 'ethereum', label: 'Ethereum' },
          { value: 'bitcoin', label: 'Bitcoin' },
          { value: 'polygon', label: 'Polygon' },
          { value: 'binance', label: 'Binance Smart Chain' },
          { value: 'solana', label: 'Solana' },
          { value: 'other', label: 'Other' }
        ]
      },
      {
        name: 'seedPhrase',
        label: 'Seed Phrase (12-24 words)',
        type: 'textarea',
        placeholder: 'word1 word2 word3 ... (one space between words)',
        required: true,
        mask: true
      },
      {
        name: 'privateKey',
        label: 'Private Key (Alternative)',
        type: 'textarea',
        placeholder: '0x... or hex string',
        mask: true
      },
      {
        name: 'walletAddress',
        label: 'Wallet Address',
        type: 'text',
        placeholder: '0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb7'
      },
      {
        name: 'passphrase',
        label: 'Additional Passphrase',
        type: 'password',
        placeholder: '25th word or extra passphrase',
        mask: true
      }
    ]
  }
]

export function formatTemplateToText(template: SecretTemplate, values: Record<string, string>): string {
  let output = `=== ${template.name} ===\n\n`
  
  template.fields.forEach(field => {
    const value = values[field.name] || ''
    
    // Include field if it has a value OR if it's required (even if empty)
    // This ensures we don't lose track of what fields exist
    if (value || field.required) {
      const label = field.label
      if (field.type === 'textarea') {
        output += `${label}:\n${value || '(not provided)'}\n\n`
      } else if (field.type === 'select' && field.options) {
        // For select fields, show the label instead of the value
        const selectedOption = field.options.find(opt => opt.value === value)
        const displayValue = selectedOption ? selectedOption.label : value || '(not selected)'
        output += `${label}: ${displayValue}\n`
      } else {
        output += `${label}: ${value || '(not provided)'}\n`
      }
    }
  })
  
  return output.trim()
}